import { useEffect, useMemo, useState } from 'react';
import Column from 'antd/lib/table/Column';
import { columnHeaderContent, headerStyle } from '../../../../../../common/TableUtils';
import { DeleteOutlined } from '@ant-design/icons';
import { injectionRowHeaderMapping } from '../../../../../../../constants';
import InjectionInstruction from './injectionColumnItems/InjectionInstruction';
import InjectionDuration from './injectionColumnItems/InjectionDuration';
import InjectionTiming from './injectionColumnItems/InjectionTiming';
import { INJECTION_COLUMN } from '../../../../../../../utils/enums';
import InjectionFrequency from './injectionColumnItems/InjectionFrequency';
import InjectionRoute from './injectionColumnItems/InjectionRoute';
import InjectionDose from './injectionColumnItems/InjectionDose';
import InjectionInfusionRate from './injectionColumnItems/InjectionInfusionRate';
import InjectionDrug from './injectionColumnItems/InjectionDrug';
import { FlatInjectionObj } from '../../../../../../../store/types/prescription';
import TableWithDragableRows from '../../TableWithDragableRows';
import { useDispatch, useSelector } from 'react-redux';
import { getInjectionElements } from '../../../../../../../store/selectors/prescriptionPad';
import {
    deleteInjection,
    swapPxElement,
    upsertInjection,
} from '../../../../../../../store/actions/prescriptionPad';
import { getInjectionFlatArray } from '../../../../../../../utils/general-utils';
import { GlobalStateV1 } from '../../../../../../../store/reducers';
import { GeniePadElementsSettingItem } from '../../../../../../../api/pushGenie';
import { getGenieSetting } from '../../../../../../../store/selectors/session';

export const INJECTIONS_TABLE_CONTAINER = 'INJECTIONS_TABLE_CONTAINER';

export const INJECTION_TABLE_ID = 'injection-table';

const renderComponent = ({
    injection,
    key,
    isDragMode,
}: {
    injection: FlatInjectionObj;
    key: INJECTION_COLUMN;
    isDragMode: boolean;
}) => {
    switch (key) {
        case INJECTION_COLUMN.INSTRUCTIONS:
            return (
                <InjectionInstruction
                    injection={injection}
                    columnKey={key}
                    isDragMode={isDragMode}
                />
            );
        case INJECTION_COLUMN.DURATION:
            return (
                <InjectionDuration injection={injection} columnKey={key} isDragMode={isDragMode} />
            );
        case INJECTION_COLUMN.TIMING:
            return (
                <InjectionTiming injection={injection} columnKey={key} isDragMode={isDragMode} />
            );
        case INJECTION_COLUMN.FREQUENCY:
            return (
                <InjectionFrequency injection={injection} columnKey={key} isDragMode={isDragMode} />
            );
        case INJECTION_COLUMN.ROUTE:
            return <InjectionRoute injection={injection} columnKey={key} />;
        case INJECTION_COLUMN.DOSE:
            return <InjectionDose injection={injection} columnKey={key} isDragMode={isDragMode} />;
        case INJECTION_COLUMN.INFUSION_RATE:
            return (
                <InjectionInfusionRate
                    injection={injection}
                    columnKey={key}
                    isDragMode={isDragMode}
                />
            );
        case INJECTION_COLUMN.DRUG:
            return <InjectionDrug injection={injection} columnKey={key} isDragMode={isDragMode} />;
        default:
            return <></>;
    }
};

const InjectionTable = (): JSX.Element => {
    const injectionTableConfig = useSelector((state) =>
        getGenieSetting(state as GlobalStateV1, 'injectionTableConfig'),
    ) as GeniePadElementsSettingItem[];
    const injectionElements = useSelector(getInjectionElements);
    const dispatch = useDispatch();
    const [genieElements, setGenieElements] = useState<FlatInjectionObj[]>();
    const [isDragMode, setIsDragMode] = useState<boolean>(false);

    useEffect(() => {
        setGenieElements(getInjectionFlatArray(injectionElements));
    }, [injectionElements]);

    const onDrag = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        dispatch(swapPxElement('injections', oldIndex, newIndex));
    };

    const configSelectedColumns = useMemo(() => {
        const requiredColumns = injectionTableConfig
            .filter((config) => !!config.isShown)
            .map((config) => config.id);
        return [INJECTION_COLUMN.DRUG, ...requiredColumns];
    }, [injectionTableConfig]);

    return (
        <div id={INJECTIONS_TABLE_CONTAINER} className="relative">
            <TableWithDragableRows
                id={INJECTION_TABLE_ID}
                tableLayout="fixed"
                className="mx-table"
                bordered={true}
                dataSource={genieElements}
                pagination={false}
                rowClassName={() => 'editable-row'}
                rowKey={(_, index) => `Injections-${index}`}
                scroll={{
                    x: 840,
                }}
                findIndexForRecordKey={(id) => {
                    return (genieElements || []).findIndex(
                        (_, index) => `Injections-${index}` === id,
                    );
                }}
                onSortEnd={({ oldIndex, newIndex }) => {
                    if (genieElements) {
                        const actualOldIndex = genieElements[oldIndex]?.mainIndex;
                        const actualNewIndex = genieElements[newIndex]?.mainIndex;
                        onDrag({ oldIndex: actualOldIndex, newIndex: actualNewIndex });
                        setIsDragMode(false);
                    }
                }}
                onSortStart={() => setIsDragMode(true)}
            >
                {configSelectedColumns.map((key) => {
                    const HeaderComponent = columnHeaderContent(
                        key,
                        injectionRowHeaderMapping[key as INJECTION_COLUMN],
                    );
                    return (
                        <Column
                            title={HeaderComponent}
                            key={key}
                            width={
                                key === INJECTION_COLUMN.DRUG
                                    ? undefined
                                    : key === INJECTION_COLUMN.INSTRUCTIONS
                                    ? '20%'
                                    : key === INJECTION_COLUMN.ROUTE
                                    ? '7%'
                                    : '10%'
                            }
                            fixed={key === INJECTION_COLUMN.DRUG ? 'left' : undefined}
                            render={(_, injection: FlatInjectionObj) =>
                                renderComponent({
                                    injection,
                                    key: key as INJECTION_COLUMN,
                                    isDragMode,
                                })
                            }
                            className={
                                key === INJECTION_COLUMN.DRUG
                                    ? 'mx-name'
                                    : key === INJECTION_COLUMN.INSTRUCTIONS
                                    ? 'w-150'
                                    : key === INJECTION_COLUMN.DURATION
                                    ? 'mx-duration'
                                    : ''
                            }
                            onCell={(injection) => {
                                if (
                                    key !== INJECTION_COLUMN.DOSE &&
                                    key !== INJECTION_COLUMN.DRUG &&
                                    injection.added_drug &&
                                    injection.added_drug.length > 0
                                ) {
                                    return {
                                        rowSpan: injection.added_drug.length + 1,
                                    };
                                }
                                if (
                                    key !== INJECTION_COLUMN.DOSE &&
                                    key !== INJECTION_COLUMN.DRUG &&
                                    injection.is_added_drug
                                ) {
                                    return {
                                        rowSpan: 0,
                                    };
                                }
                                if (
                                    key === INJECTION_COLUMN.ROUTE &&
                                    (injection.is_added_drug || injection.is_tapering_dose)
                                ) {
                                    return {
                                        rowSpan: 0,
                                    };
                                }
                                if (key === INJECTION_COLUMN.ROUTE) {
                                    return {
                                        rowSpan:
                                            Math.max(
                                                (injection.added_drug || []).length,
                                                (injection.tapering_dose || []).length,
                                            ) + 1,
                                    };
                                }
                                if (key === INJECTION_COLUMN.DRUG && injection.is_tapering_dose) {
                                    return {
                                        rowSpan: 0,
                                    };
                                }
                                if (key === INJECTION_COLUMN.DRUG) {
                                    return {
                                        rowSpan: (injection.tapering_dose || []).length + 1,
                                    };
                                }
                                return {};
                            }}
                            onHeaderCell={headerStyle}
                        />
                    );
                })}
                <Column
                    className={`p-5 delete-button`}
                    fixed="right"
                    key="delete"
                    onCell={(injection: FlatInjectionObj, rowIndex?: number) => {
                        return {};
                    }}
                    render={(_, injection) => {
                        if (isDragMode && (injection.is_added_drug || injection.is_tapering_dose)) {
                            return null;
                        }
                        return (
                            <div className="flex space-x-8 items-center justify-center">
                                <DeleteOutlined
                                    style={{ color: '#616A8F' }}
                                    size={24}
                                    onClick={() => {
                                        if (injection.is_added_drug || injection.is_tapering_dose) {
                                            const parentInjection =
                                                injectionElements[injection?.mainIndex];
                                            let updatedDoses;
                                            if (injection.is_tapering_dose) {
                                                updatedDoses = parentInjection?.tapering_dose?.filter(
                                                    (_, index) => index !== injection?.currentIndex,
                                                );
                                            } else if (injection.is_added_drug) {
                                                updatedDoses = parentInjection?.added_drug?.filter(
                                                    (_, index) => index !== injection?.currentIndex,
                                                );
                                            }

                                            if (updatedDoses !== undefined) {
                                                dispatch(
                                                    upsertInjection({
                                                        ...parentInjection,
                                                        tapering_dose: injection.is_tapering_dose
                                                            ? updatedDoses
                                                            : parentInjection.tapering_dose,
                                                        added_drug: injection.is_added_drug
                                                            ? updatedDoses
                                                            : parentInjection.added_drug,
                                                    }),
                                                );
                                            } else {
                                                console.error(
                                                    'Parent injection not found for deletion:',
                                                    injection,
                                                );
                                            }
                                        } else {
                                            dispatch(deleteInjection(injection));
                                        }
                                    }}
                                    onPointerLeaveCapture={undefined}
                                />
                            </div>
                        );
                    }}
                    onHeaderCell={headerStyle}
                />
            </TableWithDragableRows>
        </div>
    );
};

export default InjectionTable;
